/* Copyright (C) 2019 SmartboxTv S.A. - All rights reserved
 * Unauthorized copying of this file, via any medium is strictly prohibited
 * Proprietary and confidential
 * Written by Erasmo Marín <emarin@smartboxtv.com>
 */

import { observable, action, toJS, flow } from 'mobx';
import ErrorsStore from './errorsStore';
import find from 'lodash/find';
import filter from 'lodash/filter';
import get from 'lodash/get';
import set from 'lodash/set';
import _ from '../translations';
import appStore from './appStore';
import siteMapStore from './siteMapStore';
import fetch from '../fetch';
import { isError } from './actionCreator';
import merge from 'lodash/merge';
import plansStore from './plansStore';
import { cleanObject } from '../utils';
import { setBreadcrumbSentry, captureExceptionSentry } from '../utils/sentry';


class StoreAppearanceStore {
    @observable
    platforms = [];

    @action.bound
    getAllowedPlatforms = flow(function* () {
        yield plansStore.list({
            hash: 'plansList',
            params: { paginate: false },
        });
        const plans = toJS(plansStore.collection.get('plansList')) || {};
        const plan = find(plans.items, { selected: true });
        if (!plan) return false;
        this.platforms = plan.allowedPlatforms;
    });

    @action.bound
    build = flow(function* (platforms = ['ios', 'android', 'web']) {
        yield plansStore.list({
            hash: 'plansList',
            params: { paginate: false },
        });

        const plans = toJS(plansStore.collection.get('plansList')) || {};
        const plan = find(plans.items, { selected: true });

        if (!plan) return false;

        //just build platforms allowed by the current plan
        platforms = filter(
            platforms,
            platform => plan.allowedPlatforms && plan.allowedPlatforms.indexOf(platform) >= 0
        );

        const app = get(appStore, 'app._id');
        if (!app) return false;

        try {
            if (!siteMapStore.sitemap) {
                yield siteMapStore.getSiteMap();
                if (!siteMapStore.sitemap) yield siteMapStore.createDefaultSitemap();
            }
            yield this._generateAnalytics();
            const url = '/api/core/builder/build';
            const response = yield fetch.post({
                url,
                body: {
                    app,
                    platforms,
                },
            });
            if (isError(response)) {
                ErrorsStore.addError(response, _('There was an error publishing your app'));
            }
            return response;
        } catch (e) {
            ErrorsStore.addError(e, _('There was an error publishing your app'));
            console.warn(e);
            return false;
        }
    });

    @action.bound
    checkAppName = flow(function* (appName, abortCallback) {
        try {
            const url = '/api/core/builder/checkAppName';
            const result = yield fetch.post({
                url,
                body: { name: appName },
                abortCallback,
            });
            return result;
        } catch (e) {
            console.warn(e);
            return false;
        }
    });

    @action.bound
    checkDomain = flow(function* (domain, abortCallback) {
        try {
            const url = '/api/core/builder/checkDomain';
            return yield fetch.post({ url, body: { domain }, abortCallback });
        } catch (e) {
            console.warn(e);
            return false;
        }
    });

    @action.bound
    saveFormProgress = flow(function* (form) {
        try {
            let app = toJS(appStore.app);
            if (!app) return false;
            set(app, 'settings.grecaptchaScenes', []);
            merge(app, form);
            app = cleanObject(app, {
                null: true
            });

            setBreadcrumbSentry({ 
                type: 'info',
                category: 'log',
                level: 'debug',
                message : "Saving app breadcumb",
                data: { 
                    ...app,
                    section: 'app-builder/store-presence',
                    action: 'appStore.replaceApp'
                }
            });

            const response = yield appStore.replaceApp(app);
            if (get(response, 'error')) {
                captureExceptionSentry(new Error(response?.error));
                ErrorsStore.addError(response, _('There was an error saving your progress'));
                return false
            }
            yield this._generateAnalytics();
            yield appStore.getApp();

            return true;
        } catch (error) {
            captureExceptionSentry(new Error(error));
            return false;
        }
    });

    @action.bound
    _generateAnalytics = flow(function* () {
        const app = get(appStore, 'app', false);
        if (!app._id) return false;
        if (get(app, 'settings.analytics.keys')) return false;

        try {
            const url = `/api/core/builder/generateGAKeys`;
            const response = yield fetch.post({ url, body: { appId: app._id } });
            if (isError(response)) {
                ErrorsStore.addError(
                    response,
                    _('There was an error in generating the analytics for your app; try again later.')
                );
            }
            return response;
        } catch (e) {
            ErrorsStore.addError(e, _('There was an error in generating the analytics for your app; try again later.'));
            console.warn(e);
            return false;
        }
    });
}

export default new StoreAppearanceStore();
